/**
 * Coalescing toast wrapper around Sonner.
 *
 * Why this exists (Bug-067 / Bug-077): many flows fire TWO error toasts for a
 * single failure — a generic "Failed to…" (from a component catch block) plus
 * the explicit backend message (from the mutation hook's onError, via
 * getErrorMessage). The generic one overshadows the useful one.
 *
 * Rather than rewrite ~885 call sites, every module imports `toast` from here
 * instead of directly from "sonner" (enforced by app/lib/__tests__/no-direct-
 * sonner-toast.test.ts). `toast.error` coalesces within a short window so the
 * user sees ONE toast — the explicit message wins over the generic one, and
 * identical errors dedupe. `success` / `info` / `warning` / `error` also get a
 * default duration per kind (A-67, `TOAST_DURATION_MS`). Every other method
 * passes straight through.
 *
 * In test mode this is a pass-through (no coalescing, no default duration) so existing
 * `vi.mock("sonner")` assertions hold and module state can't bleed across
 * test cases. The coalescing logic itself is unit-tested via `decideErrorToast`.
 */
import { toast as sonnerToast, type ExternalToast } from "sonner";

/** Window within which a generic error toast is suppressed/upgraded. */
export const ERROR_COALESCE_WINDOW_MS = 700;

/**
 * How long each kind of toast stays (sweep A-67). Sonner's 4s default held a
 * "Saved" confirmation on screen as long as an error that has to be read — and
 * where the toast sat, that was several seconds over the header controls.
 * Confirmations clear quickly; errors stay long enough to read. `message`,
 * `loading` and `promise` keep Sonner's own behaviour.
 */
export const TOAST_DURATION_MS = {
  success: 3000,
  info: 4000,
  warning: 6000,
  error: 8000,
} as const;

type TimedToastKind = keyof typeof TOAST_DURATION_MS;

/** The kind's duration, unless the caller chose one (Infinity included). */
export function withDefaultDuration(
  kind: TimedToastKind,
  options: ExternalToast | undefined,
): ExternalToast {
  return { ...options, duration: options?.duration ?? TOAST_DURATION_MS[kind] };
}

/**
 * Generic UI fallback error strings. Kept deliberately TIGHT so real backend
 * messages (e.g. "Unable to find your organization", "Could not …") are NOT
 * misclassified as generic — only the boilerplate fallbacks are.
 */
const GENERIC_ERROR_RE =
  /^(failed to\b|something went wrong|an?\s+(unexpected\s+)?error\s+(occurred|happened|has occurred)|oops\b)/i;

export function isGenericErrorMessage(message: unknown): boolean {
  return typeof message === "string" && GENERIC_ERROR_RE.test(message.trim());
}

export type ErrorToastState = {
  at: number;
  id: string | number;
  message: string;
  generic: boolean;
} | null;

export type ErrorToastDecision =
  | { kind: "new" }
  | { kind: "drop"; id: string | number }
  | { kind: "upgrade"; id: string | number };

/**
 * Pure decision logic (unit-tested in isolation):
 *  - generic following any recent error  → drop (the overshadowing duplicate)
 *  - specific following a recent generic  → upgrade (replace generic in place)
 *  - identical specific within window     → drop (dedupe)
 *  - distinct specific within window      → new (never hide a different error)
 *  - nothing recent                       → new
 */
export function decideErrorToast(
  last: ErrorToastState,
  message: string,
  now: number,
  windowMs: number = ERROR_COALESCE_WINDOW_MS,
): ErrorToastDecision {
  if (last && now - last.at < windowMs) {
    const generic = isGenericErrorMessage(message);
    if (generic) return { kind: "drop", id: last.id };
    if (last.generic) return { kind: "upgrade", id: last.id };
    if (message === last.message) return { kind: "drop", id: last.id };
    return { kind: "new" };
  }
  return { kind: "new" };
}

let isTestEnv =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: { MODE?: string } }).env?.MODE === "test") ||
  (typeof process !== "undefined" && process.env?.NODE_ENV === "test");

/**
 * Test-only: leave (or re-enter) the test-mode pass-through, to exercise what
 * production does. `vi.stubEnv("MODE")` does not reach the flag above.
 */
export function __setToastTestMode(on: boolean): void {
  isTestEnv = on;
}

let lastErrorToast: ErrorToastState = null;

/** Test-only: reset coalescer state between cases. */
export function __resetToastCoalescer(): void {
  lastErrorToast = null;
}

type SonnerError = typeof sonnerToast.error;

const errorToast = ((...args: Parameters<SonnerError>) => {
  const message = args[0];
  // Pass through (preserving exact call arity) in tests, so vi.mock("sonner")
  // assertions still match.
  if (isTestEnv) return sonnerToast.error(...args);

  const options = withDefaultDuration("error", args[1]);
  // Non-string messages (ReactNode) are not coalesced: there is no text to compare.
  if (typeof message !== "string") return sonnerToast.error(message, options);

  const now = Date.now();
  const decision = decideErrorToast(lastErrorToast, message, now);

  if (decision.kind === "drop") {
    if (lastErrorToast) lastErrorToast.at = now;
    return decision.id;
  }

  if (decision.kind === "upgrade") {
    // Re-emit with the prior id → Sonner replaces the generic toast in place.
    const id = sonnerToast.error(message, { ...options, id: decision.id });
    lastErrorToast = { at: now, id, message, generic: false };
    return id;
  }

  const id = sonnerToast.error(message, options);
  lastErrorToast = { at: now, id, message, generic: isGenericErrorMessage(message) };
  return id;
}) as SonnerError;

/** What `success`, `info` and `warning` all take. */
type TimedArgs = Parameters<typeof sonnerToast.success>;

/**
 * `success` / `info` / `warning` with the kind's default duration. A pass-through
 * in tests for the same reason as `errorToast`: the arguments go on exactly as
 * given, so a `toHaveBeenCalledWith("Saved")` still matches.
 */
function timedToast(kind: "success" | "info" | "warning"): typeof sonnerToast.success {
  return (...args: TimedArgs) =>
    isTestEnv
      ? sonnerToast[kind](...args)
      : sonnerToast[kind](args[0], withDefaultDuration(kind, args[1]));
}

/**
 * Drop-in replacement for Sonner's `toast`: full API preserved, only `error`
 * is coalesced. Import this everywhere instead of `from "sonner"`.
 */
export const toast = Object.assign(
  (...args: Parameters<typeof sonnerToast>) => sonnerToast(...args),
  sonnerToast,
  {
    error: errorToast,
    success: timedToast("success"),
    info: timedToast("info"),
    warning: timedToast("warning"),
  },
) as typeof sonnerToast;

/**
 * Is `target` inside a toast? A toast sits OUTSIDE a modal dialog, so a click on it (to
 * read it, or to close it) is an "outside" interaction to the dialog — which then
 * dismisses itself and throws away what it was holding. A dialog whose dismissal costs
 * something passes this to `onInteractOutside` and ignores those.
 */
export function isToastElement(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("[data-sonner-toast]") !== null;
}
